import { HttpErrorResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Subject, firstValueFrom } from "rxjs";
import { DialogTemplate } from "src/app/components/global/dialog/dialog.service";
import {
  SessionChangePasswordDialogComponent,
  SessionChangePasswordDialogData,
} from "src/app/components/global/dialog/impl/session-change-password-dialog/session-change-password-dialog.component";
import {
  SessionTimedoutComponent,
  SessionTimedoutDialogData,
} from "src/app/components/global/dialog/impl/session-timedout/session-timedout.component";
import { JWTError } from "src/classes/JWTError";
import { OTPError } from "src/classes/OTPError";
import { SessionApproveResponse, SessionApproveResult } from "src/interfaces/session/session-approve-location";
import { SessionChangePasswordResponse } from "src/interfaces/session/session-change-password-response";
import { v4 as uuidv4 } from "uuid";
import { Session } from "../classes/Session";
import { ROUTES_CONFIG } from "../config/routes.config";
import { STORAGE } from "../config/storage.config";
import { LOCALES } from "../enums/locales";
import { SessionPermission } from "../enums/session-permission";
import { SessionLoginJWT } from "../interfaces/session/session-login-jwt";
import { SessionLoginPermalink } from "../interfaces/session/session-login-permalink";
import { SessionLoginQR } from "../interfaces/session/session-login-qr";
import { SessionLoginResponse, SessionOTPResponse } from "../interfaces/session/session-login-response";
import { SessionLoginTraditional } from "../interfaces/session/session-login-traditional";
import { SessionLoginRequest } from "../interfaces/session/session-loginrequest";
import { SessionLogoutResponse } from "../interfaces/session/session-logout-response";
import { SessionRecover } from "../interfaces/session/session-recover";
import { SessionRecoverResponse } from "../interfaces/session/session-recover-response";
import { LockResponse, SessionResponse } from "../interfaces/session/session-response";
import { SessionRole } from "../interfaces/session/session-role";
import { SessionWorkspace } from "../interfaces/session/session-workspace";
import { ApplicationService } from "./application.service";
import { HttpService } from "./http.service";
import { SOCKET_IN, WebSocketService } from "./websocket.service";

export interface SessionStatus {
  id: string;
  label: string;
  actionid: string;
}

export enum SESSION_IN {
  TIMEOUT = "LoginExpiryNotification",
  STO = "STO",
}

export interface SESSION_EVENT {
  event: SESSION_EVENT;
  params: Record<string, unknown>;
}

@Injectable({
  providedIn: "root",
})
export class SessionService extends Session {
  private application: ApplicationService;
  private http: HttpService;
  private socket: WebSocketService;

  public event: Subject<SESSION_EVENT>;

  public workspaces: SessionWorkspace[];
  public roles: SessionRole[];
  public statuses: SessionStatus[];

  public notifications: boolean;
  public verified: boolean;

  private handleInvalidSessionPromise: Promise<boolean> | null;

  public constructor() {
    super(uuidv4());

    this.application = inject(ApplicationService);
    this.http = inject(HttpService);
    this.socket = inject(WebSocketService);

    this.event = new Subject();

    this.workspaces = [];
    this.roles = [];
    this.statuses = [];

    this.verified = true;
    this.notifications = false;
    this.handleInvalidSessionPromise = null;

    this.socket.listen(SOCKET_IN.SESSION, (...params: unknown[]) => {
      const EVENT = <string>params.shift();

      switch (EVENT) {
        default:
          console.error("Unknown incoming session event => ", { EVENT });
          break;
      }
    });
  }

  /**
   * Force update session
   * @param tabIndex
   * @returns
   */
  public async check(skipUpdate: boolean = false): Promise<SessionResponse> {
    if (this.application.index != null) {
      return this.http.send<SessionResponse>(ROUTES_CONFIG.checkSessionUrl, {
        skipupdatelastaccessdatetime: skipUpdate,
      });
    } else {
      throw new Error("Missing tabindex");
    }
  }

  public async lock(): Promise<boolean> {
    if (this.application.index != null) {
      return this.http.send<LockResponse>(ROUTES_CONFIG.lock, {}).then((v) => v.lock === "true");
    } else {
      throw new Error("Missing tabindex");
    }
  }

  /**
   * Tries to establish a secure session
   */
  public async initialize(): Promise<void> {
    try {
      const session = await this.check();

      if (this.isInvalid(session)) {
        const result = await this.handleSessionEvent(session);
        if (result) {
          return this.initialize();
        }
      }
      await this.update(session);
      this.authenticated.next(true);
    } catch {
      this.authenticated.next(false);
      this.application.setTabIndex(null);
    }
  }

  /**
   * Open a secured session
   * @param data
   * @returns
   */
  public async connect(
    data: SessionLoginTraditional | SessionLoginQR | SessionLoginPermalink | SessionLoginJWT | SessionLoginRequest,
    url = ROUTES_CONFIG.loginUrl,
    isRelogin = false,
  ): Promise<void> {
    const isJwtLogin = "jwt" in data;
    try {
      const response = await this.http.send<SessionLoginResponse>(url, data, { bypassErrorHandling: true });
      console.error("CONNECT USER!!!! => ", response);
      if ("session" in response) {
        await this.update(response);
        this.authenticated.next(true);
      } else {
        this.application.onMessage(response.messages?.Messages ?? []);
        if (!isRelogin) {
          this.authenticated.next(false);
          this.application.setTabIndex(null);
        }
      }
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (typeof error.error === "object" && error.error !== null) {
          const isSOP = Object.keys(error.error).includes("sop");
          if (isSOP) {
            this.application.setTabIndex(error.error.tabindex);
            throw new OTPError();
          }
        }
        if (isJwtLogin) {
          throw new JWTError(error);
        }
      }

      if (!isRelogin) {
        this.authenticated.next(false);
        this.application.setTabIndex(null);
      }
      throw error;
    }
  }

  public async checkOTP(totp: string, isRelogin = false): Promise<void> {
    const response = await this.http.send<SessionLoginResponse | SessionOTPResponse>(ROUTES_CONFIG.checkotpURL, {
      totp,
      tabindex: this.application.index,
    });
    const isSOP = (response: SessionLoginResponse | SessionOTPResponse): response is SessionOTPResponse => {
      return Object.keys(response).includes("sop");
    };

    if ("session" in response) {
      await this.update(response);
      this.authenticated.next(true);
    } else if (isSOP(response)) {
      this.application.setTabIndex(response.tabindex);
      throw new OTPError();
    } else {
      this.application.onMessage(response.messages?.Messages ?? []);
      if (!isRelogin) {
        this.authenticated.next(false);
        this.application.setTabIndex(null);
        this.application.router.navigate(["logout"]);
      }
    }
  }

  /**
   * Recover account
   * @param data
   */
  public async recover(data: SessionRecover): Promise<void> {
    const res = await this.http.send<SessionRecoverResponse>(ROUTES_CONFIG.forgotpasswordurl, data);

    if (res.response && res.response.result == "OK") {
      this.application.onMessage(res.response.messages);
    } else {
      throw new Error("Invalid recovery");
    }
  }

  /**
   * Set/Update data of current session
   * @param index
   * @param data
   */
  public async update(data: SessionResponse): Promise<void> {
    if (!this.isInvalid(data)) {
      const workspaces = data.workspaces;
      const workspace = workspaces.find((workspace) => workspace.id == data.workspace.id);

      this.id = data.session.sessionid;
      this.userId = parseInt(data.user.OriginalUserID);
      this.name = data.user.displayname;
      this.permissions = this.getPermissions(data);
      console.log("UPDATING", data.user);
      this.notifications = data.user.hasnotifications || false;

      this.statuses = data.conference.statuses.map((status) => ({
        id: status.idpID,
        label: status.omsStatus,
        actionid: status.setstatusaction,
      }));

      this.status.next(data.conference.idgSchaalIDConferenceStatus);

      workspace ? localStorage.setItem(STORAGE.WORKSPACE_ID, workspace.id) : localStorage.removeItem(STORAGE.WORKSPACE_ID);

      if (workspace) {
        const role = workspace.roles.find((role) => role.id == data.role.id);
        this.workspaces = workspaces;
        this.roles = workspace.roles;
        this.role.next(role || null);
        this.workspace.next(workspace);

        role ? localStorage.setItem(STORAGE.ROLE_ID, role.id) : localStorage.removeItem(STORAGE.ROLE_ID);

        console.warn(`[SESSION] Setting session => `, {
          id: this.id,
          name: this.name,
          permissions: this.permissions,
          workspace: this.workspace.value,
          workspaces: this.workspaces,
          role: this.role.value,
          roles: this.roles,
          sessiondata: data.session,
          translationid: data.gettranslationaction,
        });
      } else {
        throw new Error(`Could not find workspace => ${data.workspace.id}`);
      }

      this.translationid.next(data.gettranslationaction);
      this.application.setTabIndex(data.session.tabindex);
      this.application.locale.next(LOCALES[data.session.lcode] || LOCALES.EN);
    } else {
      console.warn("[SESSION] Did not update session => session is invalid.");
    }
  }

  /**
   * Close the current session
   */
  public disconnect(): void {
    this.http.send<SessionLogoutResponse>(ROUTES_CONFIG.logoffUrl, { tabindex: this.application.index });
    this.id = uuidv4();
    this.name = "Guest";
    this.userId = -1;
    this.permissions = [];
    this.workspaces = [];
    this.roles = [];
    this.role.next(null);
    this.workspace.next(null);
    this.authenticated.next(false);
    this.application.setTabIndex(null);
    this.timedout.next(false);
  }

  /**
   * Change sessions role
   * @param id
   * @returns
   */
  public async changeRole(role: SessionRole): Promise<SessionResponse | void> {
    const current = await firstValueFrom(this.role);
    try {
      const response = await this.http.send<{ session: SessionResponse; sto?: string }>(ROUTES_CONFIG.changeRoleUrl, {
        currentRoleID: current?.id,
        newRoleID: role.id,
        tabindex: this.application.index,
      });

      if (this.isInvalid(response)) {
        const result = await this.handleSessionEvent(response);
        if (result) {
          return this.changeRole(role);
        }
      }

      await this.update(response.session);
      return response.session;
    } catch (err) {
      console.log(err);
      throw new Error(`Unable to change to role => ${role.id}`);
    }
  }

  /**
   * Change session workspace
   * @param id
   * @returns
   */
  public async changeWorkspace(workspace: SessionWorkspace): Promise<SessionResponse | void> {
    const current = await firstValueFrom(this.workspace);
    try {
      const response = await this.http.send<{ session: SessionResponse; sto?: string }>(ROUTES_CONFIG.changeWorkspaceUrl, {
        currentWorkspaceID: current?.id,
        newWorkspaceID: workspace.id,
        tabindex: this.application.index,
      });

      if (this.isInvalid(response)) {
        const result = await this.handleSessionEvent(response);
        if (result) {
          return this.changeWorkspace(workspace);
        }
      }

      await this.update(response.session);
      return response.session;
    } catch (err) {
      throw new Error(`Unable to change to workspace => ${workspace.id}`);
    }
  }

  public async changePassword(value: { passwordnew: string; passwordrepeat: string; FFWDActionID: string }): Promise<boolean> {
    try {
      const response = await this.http.send<SessionChangePasswordResponse>(ROUTES_CONFIG.changePasswordURL, {
        FFWDActionID: value.FFWDActionID,
        passwordnew: value.passwordnew,
        passwordrepeat: value.passwordrepeat,
        tabindex: this.application.index,
      });

      this.application.onMessage(response.messages);

      return response.messages.every((v) => v.Type !== 3);
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  /**
   * Change session status
   * @param status
   */
  public async changeStatus(status: SessionStatus): Promise<void> {
    try {
      this.status.next(status.id);
    } catch (err) {
      throw new Error(`Unable to change status => ${status.label}`);
    }
  }

  public async approveLocation(locationid: string): Promise<boolean> {
    try {
      const response = await this.http.send<SessionApproveResponse>(ROUTES_CONFIG.approvelocationurl, { locationid });
      const { result } = JSON.parse(response.response) as SessionApproveResult;
      if (result === "ok") {
        return true;
      } else {
        throw new Error("Approve Location Failed: " + JSON.stringify(response));
      }
    } catch (error) {
      return false;
    }
  }

  public async approveEmail(emailid: string): Promise<boolean> {
    try {
      const response = await this.http.send<SessionApproveResponse>(ROUTES_CONFIG.approveemailurl, { emailid });
      const { result } = JSON.parse(response.response) as SessionApproveResult;
      if (result === "ok") {
        return true;
      } else {
        throw new Error("Approve Email Failed: " + JSON.stringify(response));
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Check and set the session statuses (ex. timeout)
   * @param keys
   * @returns boolean
   */
  public async handleSessionEvent(data: Record<string, string>): Promise<boolean> {
    if (!this.handleInvalidSessionPromise) {
      this.handleInvalidSessionPromise = new Promise<boolean>((resolve) => {
        console.log("Handling session event! =>", JSON.stringify(data));
        const parsedObject = Object.fromEntries(Object.entries(data).map(([key, value]) => [key.toLowerCase(), value]));
        for (const [key, value] of Object.entries(parsedObject)) {
          switch (key) {
            case "scp":
              {
                const ref = this.application.dialog.open<SessionChangePasswordDialogData>(
                  SessionChangePasswordDialogComponent,
                  { actionId: value, title: { label: "DIALOG.SESSION_CHANGE_PASSWORD.TITLE" } },
                  DialogTemplate.FORCE,
                  {
                    disableClose: true,
                  },
                );
                firstValueFrom(ref.afterClosed()).then(() => {
                  resolve(false);
                });
              }
              break;

            case "sto":
            case "sop":
              {
                const ref = this.application.dialog.open<SessionTimedoutDialogData>(
                  SessionTimedoutComponent,
                  {
                    title: { label: "SESSION.TIMEDOUT" },
                    message: "SESSION.TIMEDOUT.MESSAGE",
                    showOTP: key === "sop",
                  },
                  DialogTemplate.DEFAULT,
                  {
                    disableClose: true,
                    backdropClass: "blur-backdrop",
                    panelClass: "no-shadow",
                  },
                );
                firstValueFrom(ref.afterClosed()).then((result: { success: boolean } | undefined) => {
                  resolve(!!result?.success);
                });
              }
              break;

            case "sco":
            case "scl":
              if (value.length > 0) {
                this.socket.disconnect();
                this.disconnect();
                resolve(false);
              }
              break;

            default:
              resolve(true);
              break;
          }
        }
      }).then((v) => {
        this.handleInvalidSessionPromise = null;
        return v;
      });
    }
    return this.handleInvalidSessionPromise;
  }

  public isInvalid(session: unknown): session is Record<string, string> {
    if (typeof session === "object" && session) {
      const parsedObject = Object.entries(session).map(([key, value]) => [key.toLowerCase(), value]);
      for (const [key, value] of parsedObject) {
        if (["scp", "sto", "sco", "scl", "sop"].includes(key) && typeof value === "string" && value) {
          console.warn("Session is invalid! =>", [key, value]);
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Get User permissions
   * @param data
   */
  private getPermissions(data: SessionResponse): SessionPermission[] {
    const permissions: SessionPermission[] = [];
    permissions.push(SessionPermission.SHOW_CONFERENCE);
    if (data.role.id === "33") permissions.push(SessionPermission.SHOW_SCHEDULES);
    if (data.role.hascockpit) permissions.push(SessionPermission.SHOW_COCKPIT);
    if (data.role.haschart) permissions.push(SessionPermission.SHOW_CHART);
    if (data.role.hascoach) permissions.push(SessionPermission.SHOW_COACH);
    if (data.role.hassearch) permissions.push(SessionPermission.SEARCH_PAGE);
    if (!data.role.donotsavetablesearch) permissions.push(SessionPermission.SAVE_TABLE_SEARCH);
    if (!data.role.donotsavetablefilters) permissions.push(SessionPermission.SAVE_TABLE_FILTERS);
    return permissions;
  }
}
